import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/api";
import { initials } from "@/components/shared";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AiAssistant } from "@/components/AiAssistant";
import {
  LayoutDashboard, MapPin, Users, UserRound, Dog, GraduationCap,
  CalendarDays, ClipboardList, BarChart3, Settings, LogOut, Lock,
  ShieldCheck, CreditCard, Sparkles, TrendingUp, Smartphone, ClipboardCheck, Activity, StickyNote, Ticket, Award,
} from "lucide-react";

const ADMIN_NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/locations", label: "Locations", icon: MapPin },
  { to: "/trainers", label: "Trainers", icon: Users },
  { to: "/clients", label: "Clients", icon: UserRound },
  { to: "/dogs", label: "Dogs", icon: Dog },
  { to: "/classes", label: "Classes", icon: CalendarDays },
  { to: "/bookings", label: "Bookings", icon: Ticket },
  { to: "/programs", label: "Training Programs", icon: GraduationCap },
  { to: "/homework", label: "Homework", icon: ClipboardList },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/quality", label: "HV Quality", icon: ShieldCheck },
  { to: "/academy", label: "HV Academy", icon: Award },
  { to: "/payments", label: "HV Payments", icon: CreditCard },
  { to: "/ai", label: "HV AI", icon: Sparkles },
  { to: "/analytics", label: "HV Analytics", icon: TrendingUp },
  { to: "/network-health", label: "Network Health", icon: Activity },
  { to: "/settings", label: "Settings", icon: Settings },
];

const MANAGER_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/trainers", label: "Trainers", icon: Users },
  { to: "/clients", label: "Clients", icon: UserRound },
  { to: "/dogs", label: "Dogs", icon: Dog },
  { to: "/classes", label: "Classes", icon: CalendarDays },
  { to: "/bookings", label: "Bookings", icon: Ticket },
  { to: "/homework", label: "Homework", icon: ClipboardList },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/quality", label: "HV Quality", icon: ShieldCheck },
  { to: "/academy", label: "HV Academy", icon: Award },
  { to: "/payments", label: "HV Payments", icon: CreditCard },
  { to: "/ai", label: "HV AI", icon: Sparkles },
  { to: "/analytics", label: "HV Analytics", icon: TrendingUp },
];

const TRAINER_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/classes", label: "My Classes", icon: CalendarDays },
  { to: "/dogs", label: "My Dogs", icon: Dog },
  { to: "/attendance", label: "Attendance", icon: ClipboardCheck },
  { to: "/homework", label: "Homework", icon: ClipboardList },
  { to: "/progress-notes", label: "Progress Notes", icon: StickyNote },
  { to: "/programs", label: "Training Programs", icon: GraduationCap },
  { to: "/academy", label: "HV Academy", icon: Award },
  { to: "/ai", label: "HV AI", icon: Sparkles },
  { to: "/analytics", label: "My Performance", icon: TrendingUp },
];

const CLIENT_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/dogs", label: "My Dogs", icon: Dog },
  { to: "/bookings", label: "My Bookings", icon: CalendarDays },
  { to: "/homework", label: "Homework", icon: ClipboardList },
  { to: "/progress", label: "Progress", icon: TrendingUp },
  { to: "/payments", label: "My Payments", icon: CreditCard },
  { to: "/ai", label: "HV AI", icon: Sparkles },
  { to: "/profile", label: "Profile", icon: UserRound },
];

const NAV_BY_ROLE = {
  super_admin: ADMIN_NAV,
  head_office_admin: ADMIN_NAV,
  franchise_owner: MANAGER_NAV,
  location_manager: MANAGER_NAV,
  trainer: TRAINER_NAV,
  client: CLIENT_NAV,
};

const FUTURE_NAV = [
  { label: "Customer App", icon: Smartphone },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role;

  const visible = NAV_BY_ROLE[role] || MANAGER_NAV;

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-border bg-card hidden md:flex flex-col fixed h-screen">
        <div className="h-16 flex items-center gap-2.5 px-6 border-b border-border">
          <div className="h-9 w-9 rounded-lg bg-neutral-900 flex items-center justify-center p-1">
            <img src="/hv-logo.png" alt="Hundeverket" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-heading font-bold text-lg leading-none text-foreground tracking-tight">Hundeverket</div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mt-0.5">Franchise OS</div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold px-3 mb-2">Operations</div>
          <div className="space-y-0.5">
            {visible.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-muted hover:text-foreground"
                  }`
                }
              >
                <item.icon size={18} strokeWidth={1.5} />
                {item.label}
              </NavLink>
            ))}
          </div>

          <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold px-3 mb-2 mt-6">Coming Soon</div>
          <div className="space-y-0.5">
            {FUTURE_NAV.map((item) => (
              <div key={item.label} className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-muted-foreground/60 cursor-not-allowed" data-testid={`nav-soon-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <item.icon size={18} strokeWidth={1.5} />
                <span className="flex-1">{item.label}</span>
                <Lock size={12} />
              </div>
            ))}
          </div>
        </nav>

        <div className="p-3 border-t border-border">
          <Badge variant="outline" className="w-full justify-center py-1.5 bg-secondary text-secondary-foreground border-transparent font-medium" data-testid="sidebar-role-badge">
            {ROLE_LABELS[role] || role}
          </Badge>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64 flex flex-col min-h-screen">
        <header className="h-16 border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-30 flex items-center justify-between px-6">
          <div className="md:hidden font-heading font-bold text-lg flex items-center gap-2 tracking-tight">
            <span className="h-8 w-8 rounded-lg bg-neutral-900 flex items-center justify-center p-1"><img src="/hv-logo.png" alt="Hundeverket" className="w-full h-full object-contain" /></span> Hundeverket
          </div>
          <div className="hidden md:block text-sm text-muted-foreground">
            Welcome back, <span className="text-foreground font-medium">{user?.full_name}</span>
          </div>
          <div className="flex items-center gap-3">
          <AiAssistant />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 hover:bg-muted rounded-full pl-1 pr-3 py-1 transition-colors" data-testid="user-menu-trigger">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">{initials(user?.full_name)}</AvatarFallback>
                </Avatar>
                <div className="text-left hidden sm:block">
                  <div className="text-sm font-medium leading-none">{user?.full_name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{ROLE_LABELS[role]}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                <Settings size={16} className="mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-destructive focus:text-destructive">
                <LogOut size={16} className="mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-6 lg:p-8 max-w-[1400px] w-full">{children}</main>
      </div>
    </div>
  );
}
